import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { 
  Fish, 
  CreditCard, 
  Camera, 
  Users, 
  Store, 
  MessageCircle, 
  Settings,
  LogOut,
  Menu,
  X,
  Award,
  Bell,
  Download
} from "lucide-react";
import { Button } from "@/components/ui/button";

const Layout = ({ children, title, subtitle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);

  // Capture the install prompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      // Show install banner if not already installed
      if (!window.matchMedia('(display-mode: standalone)').matches) {
        setShowInstallBanner(true);
      }
    };
    
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = async () => {
    if (!installPrompt) {
      // For iOS Safari
      alert('To install: Tap the Share button, then "Add to Home Screen"');
      return;
    }
    
    installPrompt.prompt();
    const result = await installPrompt.userChoice;
    if (result.outcome === 'accepted') {
      setShowInstallBanner(false);
    }
    setInstallPrompt(null);
  };

  const navItems = [
    { icon: CreditCard, label: "Dashboard", path: "/dashboard" },
    { icon: Camera, label: "Fishing Log", path: "/fishing-log" },
    { icon: Users, label: "Community", path: "/community" },
    { icon: Store, label: "Retailers", path: "/retailers" },
    { icon: Bell, label: "Messages", path: "/messages" },
    { icon: MessageCircle, label: "Q&A", path: "/qa" },
    { icon: Settings, label: "Profile", path: "/profile" },
  ];

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[var(--bg-main)]">
      {/* Install App Banner */}
      {showInstallBanner && (
        <div className="fixed bottom-0 left-0 right-0 bg-[var(--deep-ocean)] text-white p-4 z-50 flex items-center justify-between gap-4 lg:ml-64">
          <div className="flex items-center gap-3">
            <Download className="w-6 h-6" />
            <div>
              <p className="font-semibold">Install FishingFanatix</p>
              <p className="text-sm text-white/70">Add to home screen for quick access</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="text-white/70 hover:text-white hover:bg-white/10"
              onClick={() => setShowInstallBanner(false)}
            >
              Later
            </Button>
            <Button
              size="sm"
              className="bg-[var(--sunrise-orange)] text-[var(--deep-ocean)] hover:bg-[var(--sunrise-orange)]/90"
              onClick={handleInstallClick}
              data-testid="install-app-btn"
            >
              Install
            </Button>
          </div>
        </div>
      )}

      {/* Mobile Menu Overlay */}
      {menuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full w-64 bg-white card-shadow z-50 transform transition-transform lg:translate-x-0 ${
        menuOpen ? "translate-x-0" : "-translate-x-full"
      }`}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-8">
            <Link to="/dashboard" className="flex items-center gap-2">
              <img src="/logo.jpg" alt="FishingFanatix" className="h-10 w-auto" />
            </Link>
            <button 
              className="lg:hidden"
              onClick={() => setMenuOpen(false)}
            >
              <X className="w-6 h-6 text-[var(--river-stone)]" />
            </button>
          </div>

          <nav className="space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  location.pathname === item.path
                    ? "bg-[var(--deep-ocean)] text-white" 
                    : "text-[var(--river-stone)] hover:bg-[var(--bg-main)]"
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            ))}

            {user?.is_admin && (
              <Link
                to="/admin"
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  location.pathname === "/admin"
                    ? "bg-[var(--sunrise-orange)] text-[var(--deep-ocean)]"
                    : "text-[var(--sunrise-orange)] hover:bg-[var(--sunrise-orange)]/10"
                }`}
                data-testid="nav-admin"
              >
                <Award className="w-5 h-5" />
                <span className="font-medium">Admin Panel</span>
              </Link>
            )}
          </nav>

          <div className="absolute bottom-6 left-6 right-6 space-y-2">
            {/* Install App Button - always visible in sidebar */}
            {!window.matchMedia('(display-mode: standalone)').matches && (
              <Button
                variant="outline"
                className="w-full justify-start text-[var(--deep-ocean)] border-[var(--deep-ocean)]/30 hover:bg-[var(--deep-ocean)]/5"
                onClick={handleInstallClick}
                data-testid="sidebar-install-btn"
              >
                <Download className="w-5 h-5 mr-2" />
                Install App
              </Button>
            )}
            <Button
              variant="ghost"
              className="w-full justify-start text-red-500 hover:bg-red-50 hover:text-red-600"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64">
        {/* Top Bar */}
        <header className="sticky top-0 bg-white/80 backdrop-blur-lg border-b border-gray-100 z-30 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                className="lg:hidden"
                onClick={() => setMenuOpen(true)}
              >
                <Menu className="w-6 h-6 text-[var(--deep-ocean)]" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-[var(--deep-ocean)]">{title}</h1>
                {subtitle && <p className="text-sm text-[var(--river-stone)]">{subtitle}</p>}
              </div>
            </div>
            
            {user?.subscription_status !== "active" && (
              <Button
                onClick={() => navigate("/subscription")}
                className="btn-accent hidden sm:flex"
                data-testid="activate-membership-btn"
              >
                Activate Membership
              </Button>
            )}
          </div>
        </header>

        {/* Page Content */}
        <div className="p-6">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
